import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  Tooltip,
  VStack
} from '@chakra-ui/react';

const uncategorizedSkillType = '__uncategorized__';
const levelOptions = [
  { value: 'none', label: 'None' },
  { value: 'unknown', label: 'Unknown' },
  { value: '1', label: '1', tooltip: 'Limited experience, dabbled' },
  { value: '2', label: '2', tooltip: 'Getting to grips with it on a surface level' },
  { value: '3', label: '3', tooltip: 'Starting to understand in more depth. Could support on a project' },
  { value: '4', label: '4', tooltip: 'Can capably work on a project. Able to assist others with problems' },
  { value: '5', label: '5', tooltip: 'Expert. Can lead and train others' }
];

const getSkillTypeValue = (skill) => skill?.type || uncategorizedSkillType;
const getSkillTypeLabel = (value) => (value === uncategorizedSkillType ? 'Uncategorised' : value);
const getSkillLevelLabel = (level) => (level == null ? 'Unknown' : `${level}`);

const SkillLevelRow = memo(({ skill, selectedLevel, onLevelChange }) => (
  <Box borderBottomWidth="1px" borderColor="gray.100">
    <SimpleGrid
      columns={{ base: 1, md: 2 }}
      templateColumns={{ base: '1fr', md: 'minmax(200px, 2fr) minmax(360px, 3fr)' }}
      px={4}
      py={3}
      alignItems="center"
      gap={3}
    >
      <Flex direction="column" align="flex-start" textAlign="left">
        <Text>{skill.skillname}</Text>
      </Flex>
      <ButtonGroup
        isAttached
        size="sm"
        variant="outline"
        role="radiogroup"
        aria-label={`${skill.skillname} level`}
        justifySelf={{ base: 'stretch', md: 'center' }}
        width={{ base: '100%', md: 'auto' }}
      >
        {levelOptions.map((option) => {
          const isSelected = selectedLevel === option.value;

          const button = (
            <Button
              role="radio"
              aria-checked={isSelected}
              colorScheme={isSelected ? 'blue' : 'gray'}
              variant={isSelected ? 'solid' : 'outline'}
              onClick={() => onLevelChange(skill.skillid, option.value)}
              flex={{ base: 1, md: 'initial' }}
              minW={{ base: 0, md: option.value === 'unknown' ? '82px' : '56px' }}
              px={{ base: 2, md: 3 }}
            >
              {option.label}
            </Button>
          );

          return option.tooltip ? (
            <Tooltip key={`${skill.skillid}-level-${option.value}`} label={option.tooltip} hasArrow>
              {button}
            </Tooltip>
          ) : (
            <React.Fragment key={`${skill.skillid}-level-${option.value}`}>{button}</React.Fragment>
          );
        })}
      </ButtonGroup>
    </SimpleGrid>
  </Box>
));

const SkillTypeAccordionItem = memo(
  ({ type, skills, selectedCount, selectedLevelMap, isOpen, onLevelChange }) => (
    <AccordionItem borderWidth="1px" borderColor="gray.100">
      <AccordionButton bg="gray.50" _expanded={{ bg: 'blue.50' }} px={4} py={3}>
        <Flex flex="1" align="center" gap={2} textAlign="left">
          <Text fontWeight="semibold">{type.label}</Text>
          {selectedCount > 0 && (
            <Badge colorScheme="blue" borderRadius="full">
              {selectedCount}
            </Badge>
          )}
        </Flex>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel p={0}>
        {isOpen && (
          <>
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              templateColumns={{ base: '1fr', md: 'minmax(200px, 2fr) minmax(360px, 3fr)' }}
              bg="gray.50"
              px={4}
              py={2}
              fontWeight="semibold"
              borderBottomWidth="1px"
              borderColor="gray.100"
            >
              <Box textAlign="left">Skill</Box>
              <Box textAlign={{ base: 'left', md: 'center' }}>Level</Box>
            </SimpleGrid>
            {skills.map((skill) => (
              <SkillLevelRow
                key={skill.skillid}
                skill={skill}
                selectedLevel={selectedLevelMap.get(String(skill.skillid)) ?? 'none'}
                onLevelChange={onLevelChange}
              />
            ))}
          </>
        )}
      </AccordionPanel>
    </AccordionItem>
  )
);

const SkillsSection = ({
  isEditing,
  editableSkills,
  originalSkills,
  selectableSkills,
  onLevelChange,
  isLoading,
  error,
  onRetry
}) => {
  const [openSkillTypes, setOpenSkillTypes] = useState([]);
  const hasInitializedOpenTypesRef = useRef(false);

  const selectedLevelMap = useMemo(() => {
    const levelMap = new Map();

    editableSkills.forEach((skill) => {
      const selectedLevel = skill.level == null ? 'unknown' : skill.level.toString();
      levelMap.set(String(skill.skillid), selectedLevel);
    });

    return levelMap;
  }, [editableSkills]);

  const skillTypes = useMemo(() => {
    const typeMap = new Map();

    selectableSkills.forEach((skill) => {
      const value = getSkillTypeValue(skill);
      if (!typeMap.has(value)) {
        typeMap.set(value, getSkillTypeLabel(value));
      }
    });

    return Array.from(typeMap, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [selectableSkills]);

  const skillsByType = useMemo(() => {
    const groupedSkills = new Map();

    selectableSkills.forEach((skill) => {
      const typeValue = getSkillTypeValue(skill);
      const skillsForType = groupedSkills.get(typeValue) ?? [];
      skillsForType.push(skill);
      groupedSkills.set(typeValue, skillsForType);
    });

    groupedSkills.forEach((skills) => {
      skills.sort((a, b) => a.skillname.localeCompare(b.skillname));
    });

    return groupedSkills;
  }, [selectableSkills]);

  useEffect(() => {
    if (hasInitializedOpenTypesRef.current) {
      return;
    }

    setOpenSkillTypes((prev) => {
      if (prev.length > 0) {
        hasInitializedOpenTypesRef.current = true;
        return prev;
      }

      const typesFromSelectedSkills = editableSkills.map(getSkillTypeValue);
      hasInitializedOpenTypesRef.current = true;
      return Array.from(new Set(typesFromSelectedSkills));
    });
  }, [editableSkills]);

  const openAccordionIndexes = useMemo(
    () =>
      openSkillTypes
        .map((typeValue) => skillTypes.findIndex((type) => type.value === typeValue))
        .filter((index) => index >= 0),
    [openSkillTypes, skillTypes]
  );

  const selectedCountByType = useMemo(() => {
    const counts = new Map();

    selectableSkills.forEach((skill) => {
      if (!selectedLevelMap.has(String(skill.skillid))) {
        return;
      }
      const typeValue = getSkillTypeValue(skill);
      counts.set(typeValue, (counts.get(typeValue) ?? 0) + 1);
    });

    return counts;
  }, [selectableSkills, selectedLevelMap]);

  const openSkillTypeSet = useMemo(() => new Set(openSkillTypes), [openSkillTypes]);

  const handleAccordionChange = useCallback((nextIndexes) => {
    const normalizedIndexes = Array.isArray(nextIndexes) ? nextIndexes : [nextIndexes];
    setOpenSkillTypes(
      normalizedIndexes
        .map((index) => skillTypes[index]?.value)
        .filter(Boolean)
    );
  }, [skillTypes]);

  return (
    <Box bg="white" boxShadow="md" borderRadius="md" p={6}>
      <Heading as="h2" size="md" mb={2}>
        Key skills
      </Heading>
      {isEditing ? (
        <>
          {isLoading && (
            <Text fontSize="sm" color="gray.500">
              Loading skills...
            </Text>
          )}
          {error && !isLoading && (
            <Flex align="center" gap={2}>
              <Text fontSize="sm" color="red.500">
                {error}
              </Text>
              <Button size="xs" variant="outline" onClick={onRetry}>
                Retry
              </Button>
            </Flex>
          )}
          {!isLoading && !error && skillTypes.length === 0 && <Text mt={4}>No skills available.</Text>}
          {!isLoading && !error && skillTypes.length > 0 && (
            <Accordion allowMultiple index={openAccordionIndexes} onChange={handleAccordionChange} mt={4}>
              {skillTypes.map((type) => {
                const skillsForType = skillsByType.get(type.value) ?? [];

                return (
                  <SkillTypeAccordionItem
                    key={type.value}
                    type={type}
                    skills={skillsForType}
                    selectedCount={selectedCountByType.get(type.value) ?? 0}
                    selectedLevelMap={selectedLevelMap}
                    isOpen={openSkillTypeSet.has(type.value)}
                    onLevelChange={onLevelChange}
                  />
                );
              })}
            </Accordion>
          )}
        </>
      ) : originalSkills.length > 0 ? (
        <VStack align="start" spacing={1}>
          {originalSkills.map((skill) => (
            <Text key={skill.skillid}>
              - {skill.skillname}
              <Badge colorScheme="blue" ml={2}>
                {getSkillLevelLabel(skill.level)}
              </Badge>
            </Text>
          ))}
        </VStack>
      ) : (
        <Text>No technical skills listed.</Text>
      )}
    </Box>
  );
};

export default SkillsSection;
